import React from "react"
import type { ReactNode } from "react"
import cx from "classnames"
import Icon from "../icon"
import type { IconName } from "../icon"
import { Button } from "../button"
import { LinkButton } from "../link-button"
import type { ButtonVariant } from "../button-base"
import type { TelemetryValue } from "../event-emitter/telemetry-context"
import { cn } from "../utils"

/**
 * The rewritten note is the named export, reached on the path consumers
 * already import from:
 *
 *     import Note from "@kit/design/note"    // legacy, below
 *     import { Note } from "@kit/design/note" // rewritten
 *
 * A call site migrates by changing that one line and its props, and this file
 * disappears once none of them import the default any more.
 */
export { Note } from "../note-new"
export type { NoteProps } from "../note-new"

/** @deprecated Use the `variant` prop on the named `Note`. See the module comment. */
export type NoteType =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "generic-light"

/** @deprecated Use the `layout` prop on the named `Note`. See the module comment. */
export type NoteLayout = "inline" | "stacked"

type NoteTypeConfig = {
  bg: string
  iconColor: string
  icon: IconName
  buttonColor: string
}

const buttonStyles = ["rounded-sm p-1 px-3 text-sm font-semibold leading-6"]

/** @deprecated The named `Note` styles itself from its `variant` prop. See the module comment. */
export const types: Record<NoteType, NoteTypeConfig> = {
  success: {
    bg: "bg-green-100",
    iconColor: "text-green-700",
    icon: "check-circle",
    buttonColor: "bg-green-400 text-black hover:bg-green-500 focus:bg-green-500"
  },
  warning: {
    bg: "bg-orange-100",
    iconColor: "text-orange-700",
    icon: "alert-circle",
    buttonColor: "bg-orange-400 hover:bg-orange-400"
  },
  info: {
    bg: "bg-blue-100",
    iconColor: "text-blue-700",
    icon: "information-circle",
    buttonColor: "bg-blue-400 hover:bg-blue-500"
  },
  error: {
    bg: "bg-red-100",
    iconColor: "text-red-700",
    icon: "alert-triangle",
    buttonColor: "bg-red-400 hover:bg-red-500"
  },
  "generic-light": {
    bg: "bg-warm-white-100",
    iconColor: "text-gray-900",
    icon: "lightbulb",
    buttonColor: "bg-gray-900 text-white hover:bg-gray-800"
  }
}

const sizes: Record<string, string> = {
  sm: "py-2 px-4 items-center",
  lg: "p-4"
}

/** @deprecated Put a `Button` or `LinkButton` in `Note.Actions` on the named `Note`. See the module comment. */
export type NoteAction = {
  /** Button text */
  label?: string
  /** Click handler for the action button */
  onClick?: () => void
  /** Link URL for the action button */
  href?: string
  /** Link target attribute */
  target?: string
  /** Telemetry data for the action */
  telemetry?: TelemetryValue
  /** `Button` treatment. */
  variant?: Exclude<ButtonVariant, "unstyled">
  /** Renders the action disabled. Ignored when `href` is set. */
  disabled?: boolean
}

/** @deprecated Import the named `Note` instead. See the module comment. */
export type LegacyNoteProps = {
  /** Color/style variant of the note */
  type?: NoteType
  /** Size of the note layout */
  size?: "sm" | "lg"
  /**
   * Placement of the leading element. `inline` puts it to the left of the text,
   * `stacked` puts it on its own line above the title.
   */
  layout?: NoteLayout
  /** Title content displayed above the message. Only available on `lg` size. */
  title?: ReactNode
  /** Body text of the note */
  message?: string
  /** Action button configuration */
  action?: NoteAction
  /** Controls icon display. Set to `false` to hide, or pass a string for a custom icon name. */
  icon?: boolean | IconName
  /** Element rendered in the leading position instead of the icon. */
  leading?: ReactNode
  /** Additional CSS classes */
  className?: string
  /** Custom content rendered inside the note */
  children?: ReactNode
}

/**
 * A contextual banner for displaying informational, success, warning, or error messages.
 * Supports an optional title, action button, and custom icon.
 *
 * @deprecated Import the named `Note` instead. See the module comment.
 */
function LegacyNote({
  type = "info",
  size = "lg",
  layout = "inline",
  title,
  message,
  action,
  children,
  icon = true,
  leading,
  className: classNameProp
}: LegacyNoteProps) {
  const stacked = layout === "stacked"

  const className = cn(
    `rounded-lg text-sm text-black flex`,
    stacked && "flex-col",
    types[type].bg,
    sizes[size],
    classNameProp
  )

  const iconName = typeof icon === "string" ? icon : types[type].icon
  const leadingSpacing = stacked ? "mb-4" : "mr-4"

  return (
    <div className={className} data-testid="note" style={{ minHeight: "48px" }}>
      {leading ? (
        <div className={leadingSpacing} data-slot="note-leading">
          {leading}
        </div>
      ) : (
        icon && (
          <Icon
            name={iconName}
            className={cn(types[type].iconColor, leadingSpacing)}
          />
        )
      )}
      <div className={cn("w-full", size === "sm" && "flex items-center")}>
        {size !== "sm" && title && (
          <Title className="flex-shrink-0">{title}</Title>
        )}
        {message && (
          <p
            className={cx("text-black leading-6", size === "sm" && "flex-grow")}
          >
            {message}
          </p>
        )}
        {action && (
          <Action
            action={action}
            type={type}
            className={cn({
              "mt-4": size !== "sm",
              "ml-4": size === "sm"
            })}
          />
        )}
        {children}
      </div>
    </div>
  )
}

type ActionProps = {
  action: NoteAction
  type: NoteType
  className?: string
}

function Action({ action, type, className }: ActionProps) {
  const { label, onClick, href, target, telemetry, variant, disabled } = action

  const styleProps = variant
    ? ({ variant, size: "small", className } as const)
    : ({
        variant: "unstyled",
        className: cn(buttonStyles, types[type].buttonColor, className)
      } as const)

  if (href) {
    return (
      <LinkButton
        {...styleProps}
        href={href}
        onClick={onClick}
        target={target}
        telemetry={telemetry}
      >
        {label}
      </LinkButton>
    )
  }

  return (
    <Button
      {...styleProps}
      onClick={onClick}
      disabled={disabled}
      telemetry={telemetry}
    >
      {label}
    </Button>
  )
}

type TitleProps = {
  children?: ReactNode
  className?: string
}

/** @deprecated Use `Note.Heading` on the named `Note`. See the module comment. */
export function Title({ children, className: classNameProp }: TitleProps) {
  const className = cn(
    "font-semibold text-sm text-black leading-6 mb-1",
    classNameProp
  )

  return <div className={className}>{children}</div>
}

export default LegacyNote
